import {
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "../../lib/cn";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Footer content (e.g. buttons). Sticky at bottom. */
  footer?: ReactNode;
  /** Max width. Default: 1000px */
  maxWidth?: string;
  /** Max height for body. Default: 85vh */
  maxHeight?: string;
  /** If true, show full-screen on mobile */
  fullScreenOnMobile?: boolean;
  /** Mobile-only presentation. Desktop rendering is unchanged. */
  mobilePresentation?: "screen" | "sheet";
  /** Called when user tries to close (X, ESC, overlay). If returns false or shows confirm, caller handles. */
  onCloseAttempt?: () => void | boolean | Promise<boolean>;
  /** Accessibility: aria-describedby for subtitle */
  "aria-describedby"?: string;
  /** Aria label for close button. Default: "Close" */
  closeLabel?: string;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  subtitle,
  children,
  footer,
  maxWidth = "1000px",
  maxHeight = "85vh",
  fullScreenOnMobile = true,
  mobilePresentation = "screen",
  onCloseAttempt,
  "aria-describedby": ariaDescribedBy,
  closeLabel = "Close",
}: DialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    if (onCloseAttempt) {
      const result = onCloseAttempt();
      if (result === false) return;
      if (result instanceof Promise) {
        result.then((ok) => {
          if (ok !== false) onOpenChange(false);
        });
        return;
      }
    }
    onOpenChange(false);
  }, [onOpenChange, onCloseAttempt]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, handleClose]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      aria-describedby={ariaDescribedBy}
      className={cn(
        "fixed inset-0 z-[9999] flex p-0 sm:items-center sm:justify-center sm:p-4",
        mobilePresentation === "sheet" ? "items-end justify-center" : "items-center justify-center",
      )}
      onClick={(e) => {
        if (e.target === overlayRef.current) handleClose();
      }}
    >
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div
        ref={contentRef}
        className={cn(
          "relative z-10 flex w-full max-w-[1100px] flex-col overflow-hidden border-0 bg-background shadow-2xl sm:max-h-[90vh] sm:rounded-xl sm:border",
          mobilePresentation === "sheet"
            ? "of-mobile-dialog-sheet max-h-[88dvh] rounded-t-[20px] border-t border-sidebar-border sm:max-h-[90vh] sm:rounded-xl sm:border"
            : "rounded-none",
          fullScreenOnMobile && mobilePresentation === "screen" && "h-full sm:h-auto",
          (!fullScreenOnMobile || mobilePresentation === "sheet") && "max-h-[90vh]",
        )}
        style={maxWidth !== "1000px" ? { maxWidth } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {mobilePresentation === "sheet" ? (
          <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-[#D9D6CC] sm:hidden" aria-hidden="true" />
        ) : null}
        <div
          className={cn(
            "flex shrink-0 flex-col gap-0.5 border-b px-4 py-3 sm:px-6",
            fullScreenOnMobile && mobilePresentation === "screen" ? "sm:rounded-t-xl" : "rounded-t-xl",
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h2
                id="dialog-title"
                className="text-lg font-semibold leading-tight sm:text-xl"
              >
                {title}
              </h2>
              {subtitle ? (
                <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="-mr-2 shrink-0 rounded-chrome p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={closeLabel}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto"
          style={{ maxHeight: fullScreenOnMobile ? undefined : maxHeight }}
        >
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            {children}
          </div>
        </div>

        {footer ? (
          <div
            className={cn(
              "flex shrink-0 flex-wrap items-center justify-end gap-2 border-t bg-muted/30 px-4 py-3 pb-[calc(12px+env(safe-area-inset-bottom,0px))] sm:px-6 sm:pb-3",
              fullScreenOnMobile ? "sm:rounded-b-xl" : "rounded-b-xl"
            )}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
